import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Chip,
  Link,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import { AdminLayout } from '../components/AdminLayout';
import FunnelAnalytics from '../components/reports/FunnelAnalytics';

interface AccountRow {
  userId: string;
  username: string;
  displayName: string;
  totalRooms: number;
  pendingCount: number;
  uncompletedCount: number;
}

interface RoomRow {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  participantName: string;
  participantPhone: string;
  createdByName: string;
  createdBy: string;
}

interface RoomDetailRow {
  id: string;
  roomNumber: number | null;
  title: string;
  status: string;
  projectNo: string;
  createdAt: string;
  completedAt: string | null;
  closedAt: string | null;
  createdByName: string;
  participantName: string;
  adminMessageCount: number;
  enduserMessageCount: number;
  totalMessageCount: number;
  firstMessageAt: string | null;
  lastMessageAt: string | null;
  elapsedHours: number;
  handlingSeconds: number;
}

interface RoomDetailSummary {
  totalRooms: number;
  completedRooms: number;
  totalAdminMessages: number;
  totalEnduserMessages: number;
  totalMessages: number;
}

interface Totals {
  total: number;
  pending: number;
  uncompleted: number;
}

const STATUS_LABELS: Record<string, string> = {
  pending: '未対応',
  active: '対応中',
  waiting: '返信待ち',
  paused: '保留',
  completed: '完了',
  temp_completed: '一時完了',
  archived: '削除',
  closed: 'クローズ',
  development: '開発',
};

const STATUS_COLORS: Record<string, 'error' | 'primary' | 'warning' | 'info' | 'success' | 'default'> = {
  pending: 'error',
  active: 'primary',
  waiting: 'info',
  paused: 'warning',
  completed: 'success',
  temp_completed: 'info',
  archived: 'default',
};

type TabValue = 'rooms' | 'accounts' | 'room-detail' | 'funnel';

const Reports: React.FC = () => {
  const [tab, setTab] = useState<TabValue>('accounts');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accountName, setAccountName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [rooms, setRooms] = useState<RoomRow[]>([]);
  const [roomDetails, setRoomDetails] = useState<RoomDetailRow[]>([]);
  const [roomDetailSummary, setRoomDetailSummary] = useState<RoomDetailSummary>({
    totalRooms: 0, completedRooms: 0, totalAdminMessages: 0, totalEnduserMessages: 0, totalMessages: 0,
  });
  const [totals, setTotals] = useState<Totals>({ total: 0, pending: 0, uncompleted: 0 });
  const [hasSearched, setHasSearched] = useState(false);
  const [funnelRefreshTrigger, setFunnelRefreshTrigger] = useState(0);

  const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const buildQuery = (): string => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (accountName.trim()) params.set('accountName', accountName.trim());
    return params.toString();
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = buildQuery();
      if (tab === 'accounts') {
        const res = await fetch(`/api/reports/account-summary?${query}`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('データの取得に失敗しました');
        const data = await res.json();
        setAccounts(data.accounts || []);
        setTotals(data.totals || { total: 0, pending: 0, uncompleted: 0 });
      } else if (tab === 'room-detail') {
        const res = await fetch(`/api/reports/room-detail?${query}`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('データの取得に失敗しました');
        const data = await res.json();
        setRoomDetails(data.rooms || []);
        setRoomDetailSummary(data.summary || {
          totalRooms: 0, completedRooms: 0, totalAdminMessages: 0, totalEnduserMessages: 0, totalMessages: 0,
        });
      } else {
        const res = await fetch(`/api/reports/room-list?${query}`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('データの取得に失敗しました');
        const data = await res.json();
        setRooms(data.rooms || []);
        setTotals(data.totals || { total: 0, pending: 0, uncompleted: 0 });
      }
      setHasSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [tab, startDate, endDate, accountName]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    fetchData();
    setFunnelRefreshTrigger((prev) => prev + 1);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setTab(newValue);
  };

  const handleCSVExport = async (format: 'utf8bom' | 'shiftjis') => {
    try {
      const query = buildQuery();
      const url = `/api/reports/export?type=room-detail&format=${format}&${query}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('CSV出力に失敗しました');
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `CSチャット_ルーム詳細レポート_${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CSV出力エラー');
    }
  };

  useEffect(() => {
    if (hasSearched) {
      fetchData();
    }
  }, [tab]);

  const buildChatLink = (category: string): string => {
    return `/admin?category=${encodeURIComponent(category)}`;
  };

  return (
    <AdminLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* ヘッダー */}
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <AssessmentIcon />
          レポート
        </Typography>

        {/* 検索フォーム */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              label="開始日"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="終了日"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="アカウント名"
              size="small"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="部分一致検索"
              sx={{ minWidth: 180 }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              検索
            </Button>
          </Box>
        </Paper>

        {/* 総合計カード */}
        {hasSearched && tab !== 'room-detail' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">総件数</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {totals.total}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: '#ffebee', border: '1px solid #f44336' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">未対応</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                  {totals.pending} <Typography component="span" variant="body1" color="text.secondary">/ {totals.total}</Typography>
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">未完了</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                  {totals.uncompleted} <Typography component="span" variant="body1" color="text.secondary">/ {totals.total}</Typography>
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* ルーム詳細レポート用サマリーカード */}
        {hasSearched && tab === 'room-detail' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">総ルーム数</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {roomDetailSummary.totalRooms}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: '#e8f5e9' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">完了ルーム数</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                  {roomDetailSummary.completedRooms}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: '#e8eaf6' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">運営メッセージ</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#3f51b5' }}>
                  {roomDetailSummary.totalAdminMessages}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: '#fff3e0' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">顧客メッセージ</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                  {roomDetailSummary.totalEnduserMessages}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: '#fce4ec' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">合計メッセージ</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#e91e63' }}>
                  {roomDetailSummary.totalMessages}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* タブ切替 */}
        <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="アカウント一覧" value="accounts" />
          <Tab label="ルーム一覧" value="rooms" />
          <Tab label="ルーム詳細レポート" value="room-detail" />
          <Tab label="ファネル分析" value="funnel" />
        </Tabs>

        {/* CSV出力ボタン（ルーム詳細タブのみ） */}
        {tab === 'room-detail' && hasSearched && roomDetails.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleCSVExport('utf8bom')}
            >
              CSV (UTF-8)
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleCSVExport('shiftjis')}
            >
              CSV (Shift-JIS)
            </Button>
          </Box>
        )}

        {/* ファネル分析タブ */}
        {tab === 'funnel' ? (
          <FunnelAnalytics
            startDate={startDate || undefined}
            endDate={endDate || undefined}
            refreshTrigger={funnelRefreshTrigger}
          />
        ) : (
          /* テーブル */
          <Paper>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : tab === 'accounts' ? (
              <AccountTable accounts={accounts} totals={totals} buildChatLink={buildChatLink} />
            ) : tab === 'room-detail' ? (
              <RoomDetailTable rooms={roomDetails} />
            ) : (
              <RoomTable rooms={rooms} />
            )}
          </Paper>
        )}
      </Box>
    </AdminLayout>
  );
};

/* ---------- アカウント一覧テーブル ---------- */
interface AccountTableProps {
  accounts: AccountRow[];
  totals: Totals;
  buildChatLink: (category: string) => string;
}

const linkSx = {
  cursor: 'pointer',
  textDecoration: 'none',
  borderBottom: '1px dashed',
  borderColor: 'inherit',
  opacity: 0.85,
  '&:hover': { opacity: 1, borderBottomStyle: 'solid' },
};

const AccountTable: React.FC<AccountTableProps> = ({ accounts, totals, buildChatLink }) => {
  if (accounts.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">該当するアカウントがありません</Typography>
      </Box>
    );
  }

  const sorted = [...accounts].sort((a, b) => b.pendingCount - a.pendingCount);

  return (
    <TableContainer sx={{ maxHeight: 600 }}>
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#f5f5f5' }}>アカウント名</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#f5f5f5' }}>作成チャット件数</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#f5f5f5' }}>未対応件数</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#f5f5f5' }}>未完了件数</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((a) => (
            <TableRow key={a.userId} hover>
              <TableCell sx={{ fontSize: '1rem' }}>
                <Link
                  href={buildChatLink(`operator:${a.userId}`)}
                  underline="none"
                  color="inherit"
                  sx={{ ...linkSx, borderColor: 'text.secondary' }}
                >
                  {a.displayName || a.username}
                </Link>
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '1.1rem' }}>
                <Link
                  href={buildChatLink(`operator:${a.userId}`)}
                  underline="none"
                  color="inherit"
                  sx={{ ...linkSx, fontWeight: 'bold', borderColor: 'text.secondary' }}
                >
                  {a.totalRooms}
                </Link>
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '1.1rem' }}>
                <Link
                  href={buildChatLink(`operator:${a.userId}`)}
                  underline="none"
                  sx={{ ...linkSx, fontWeight: 'bold', color: '#f44336', borderColor: '#f44336' }}
                >
                  {a.pendingCount}
                </Link>
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '1.1rem' }}>
                <Link
                  href={buildChatLink(`operator:${a.userId}`)}
                  underline="none"
                  sx={{ ...linkSx, fontWeight: 'bold', color: '#ff9800', borderColor: '#ff9800' }}
                >
                  {a.uncompletedCount}
                </Link>
              </TableCell>
            </TableRow>
          ))}
          {/* 合計行 */}
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>合計</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
              {sorted.reduce((s, a) => s + a.totalRooms, 0)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#f44336' }}>
              {totals.pending}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#ff9800' }}>
              {totals.uncompleted}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/* ---------- ルーム一覧テーブル ---------- */
interface RoomTableProps {
  rooms: RoomRow[];
}

const RoomTable: React.FC<RoomTableProps> = ({ rooms }) => {
  if (rooms.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">該当するルームがありません</Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 600 }}>
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#f5f5f5' }}>タイトル</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#f5f5f5' }}>ステータス</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#f5f5f5' }}>参加者名</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#f5f5f5' }}>作成者</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', bgcolor: '#f5f5f5' }}>作成日時</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rooms.map((room) => (
            <TableRow key={room.id} hover>
              <TableCell sx={{ fontSize: '1rem' }}>
                <Link
                  href={`/admin?room=${room.id}`}
                  underline="none"
                  color="inherit"
                  sx={{ ...linkSx, borderColor: 'text.secondary' }}
                >
                  {room.title || '(無題)'}
                </Link>
              </TableCell>
              <TableCell>
                <Chip
                  label={STATUS_LABELS[room.status] || room.status}
                  color={STATUS_COLORS[room.status] || 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ fontSize: '1rem' }}>{room.participantName || '-'}</TableCell>
              <TableCell sx={{ fontSize: '1rem' }}>{room.createdByName}</TableCell>
              <TableCell sx={{ fontSize: '0.95rem' }}>{new Date(room.createdAt).toLocaleString('ja-JP')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/* ---------- ルーム詳細レポートテーブル ---------- */
interface RoomDetailTableProps {
  rooms: RoomDetailRow[];
}

const formatDateTime = (d: string | null): string => {
  if (!d) return '-';
  return new Date(d).toLocaleString('ja-JP');
};

const RoomDetailTable: React.FC<RoomDetailTableProps> = ({ rooms }) => {
  if (rooms.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">該当するルームがありません</Typography>
      </Box>
    );
  }

  const headerSx = { fontWeight: 'bold', fontSize: '0.85rem', bgcolor: '#f5f5f5', whiteSpace: 'nowrap' as const };

  return (
    <TableContainer sx={{ maxHeight: 600 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx}>No</TableCell>
            <TableCell sx={headerSx}>案件No</TableCell>
            <TableCell sx={headerSx}>ルーム名</TableCell>
            <TableCell sx={headerSx}>ステータス</TableCell>
            <TableCell sx={headerSx}>作成者</TableCell>
            <TableCell sx={headerSx}>顧客名</TableCell>
            <TableCell sx={headerSx}>開始日時</TableCell>
            <TableCell sx={headerSx}>完了日時</TableCell>
            <TableCell align="right" sx={headerSx}>経過</TableCell>
            <TableCell align="right" sx={headerSx}>対応時間</TableCell>
            <TableCell align="right" sx={headerSx}>運営</TableCell>
            <TableCell align="right" sx={headerSx}>顧客</TableCell>
            <TableCell align="right" sx={headerSx}>合計</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rooms.map((room) => (
            <TableRow key={room.id} hover>
              <TableCell sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap', fontWeight: 'bold', color: '#1976d2' }}>
                {room.roomNumber || '-'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                {room.projectNo || '-'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.85rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Tooltip title={room.title || '(無題)'}>
                  <Link
                    href={`/admin?room=${room.id}`}
                    underline="none"
                    color="inherit"
                    sx={{ ...linkSx, borderColor: 'text.secondary' }}
                  >
                    {room.title || '(無題)'}
                  </Link>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Chip
                  label={STATUS_LABELS[room.status] || room.status}
                  color={STATUS_COLORS[room.status] || 'default'}
                  size="small"
                  sx={room.status === 'archived' ? { color: '#000' } : undefined}
                />
              </TableCell>
              <TableCell sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{room.createdByName}</TableCell>
              <TableCell sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{room.participantName || '-'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatDateTime(room.createdAt)}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatDateTime(room.completedAt)}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap', color: room.status === 'archived' ? 'text.primary' : room.completedAt ? 'text.primary' : '#e65100' }}>
                {room.elapsedHours >= 24 ? `${Math.floor(room.elapsedHours / 24)}日` : `${room.elapsedHours.toFixed(1)}h`}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap', color: room.handlingSeconds > 0 ? '#2e7d32' : 'text.secondary' }}>
                {room.handlingSeconds > 0 ? `${Math.floor(room.handlingSeconds / 60)}分${String(room.handlingSeconds % 60).padStart(2, '0')}秒` : '-'}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#3f51b5' }}>
                {room.adminMessageCount}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#ff9800' }}>
                {room.enduserMessageCount}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                {room.totalMessageCount}
              </TableCell>
            </TableRow>
          ))}
          {/* 合計行 */}
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell />
            <TableCell sx={{ fontWeight: 'bold' }}>合計</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{rooms.length}件</TableCell>
            <TableCell colSpan={7} />
            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#3f51b5' }}>
              {rooms.reduce((s, r) => s + r.adminMessageCount, 0)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
              {rooms.reduce((s, r) => s + r.enduserMessageCount, 0)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {rooms.reduce((s, r) => s + r.totalMessageCount, 0)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Reports;
